// TOVIS · Módulo TUBITOS · campo "Nº da 1ª etiqueta" dentro da Seção 2 (Laboratório) do FAD-01.
// Salva por caminho próprio (/api/tubitos), separado do boletim: não mexe no salvar do FAD-01.
// A sugestão NUNCA é gravada sozinha: só depois que o Walber confirma (botão ou digitando).
// Depois de confirmado, salva junto quando o boletim é salvo (evento 'tovis:fad01-salvo').
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { tubitosApi, TUBITOS_COR, C } from './tubitosApi';
import type { InfoBoletim } from './tubitosApi';

type Props = { fad01Id?: number | null; qtdTela?: number | string | null };

export default function CampoEtiquetaTubitos({ fad01Id, qtdTela }: Props) {
  const [info, setInfo] = useState<InfoBoletim | null>(null);
  const [conf, setConf] = useState<InfoBoletim | null>(null);
  const [valor, setValor] = useState('');
  const [confirmado, setConfirmado] = useState(false);
  const [salvo, setSalvo] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    if (!fad01Id) return;
    setCarregando(true); setErro('');
    try {
      const i = await tubitosApi.boletim(fad01Id);
      setInfo(i);
      setSalvo(i.numero_inicial);
      setConfirmado(i.numero_inicial !== null);
      setValor(i.numero_inicial !== null ? String(i.numero_inicial) : i.sugestao !== null ? String(i.sugestao) : '');
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível carregar o nº da etiqueta.');
    } finally {
      setCarregando(false);
    }
  }, [fad01Id]);
  useEffect(() => { carregar(); }, [carregar]);

  const limpo = valor.trim();
  const numero = /^\d{1,3}$/.test(limpo) ? Number(limpo) : null;
  const valido = numero !== null && numero >= 1 && numero <= 999;

  useEffect(() => {
    if (!fad01Id || !info || !info.mostrar_campo || !valido) { setConf(null); return; }
    const t = setTimeout(() => {
      tubitosApi.conferir(fad01Id, numero as number).then(setConf).catch((e: any) => setErro(e?.message || ''));
    }, 350);
    return () => clearTimeout(t);
  }, [fad01Id, info, valido, numero]);

  const salvar = useCallback(async () => {
    if (!fad01Id || !valido) return;
    setSalvando(true); setErro('');
    try {
      const r = await tubitosApi.salvar(fad01Id, numero as number);
      setSalvo(numero); setConfirmado(true); setConf(r);
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível salvar o nº da etiqueta.');
    } finally {
      setSalvando(false);
    }
  }, [fad01Id, valido, numero]);

  const pendente = confirmado && valido && numero !== salvo;
  const atual = useRef({ pendente, salvar, carregar });
  atual.current = { pendente, salvar, carregar };
  useEffect(() => {
    const ouvir = async (ev: Event) => {
      const id = (ev as CustomEvent).detail?.id;
      if (!fad01Id || Number(id) !== Number(fad01Id)) return;
      if (atual.current.pendente) await atual.current.salvar();
      await atual.current.carregar();
    };
    window.addEventListener('tovis:fad01-salvo', ouvir);
    return () => window.removeEventListener('tovis:fad01-salvo', ouvir);
  }, [fad01Id]);

  const remover = async () => {
    if (!fad01Id) return;
    if (!window.confirm('Apagar o nº da etiqueta deste boletim?')) return;
    try { await tubitosApi.remover(fad01Id); await carregar(); } catch (e: any) { setErro(e?.message || ''); }
  };

  const caixa = { bgcolor: '#fff', border: `1px solid ${C.borda}`, borderRadius: '14px', p: 2, mb: 2, color: C.texto } as const;

  if (!fad01Id) {
    return <Box sx={{ ...caixa, fontSize: 15, color: C.fraco }}><b style={{ color: C.texto }}>Nº da etiqueta dos tubitos:</b> salve o boletim primeiro.</Box>;
  }
  if (carregando && !info) return <Box sx={{ py: 1 }}><CircularProgress size={20} /></Box>;
  if (!info) return erro ? <Box sx={{ color: C.vermelho, fontSize: 14, mb: 2 }}>{erro}</Box> : null;

  if (!info.mostrar_campo) {
    if (info.numero_inicial === null) return null;
    return (
      <Box sx={{ ...caixa, border: `2px solid ${C.ambar}`, bgcolor: C.ambarFundo, color: C.ambarTexto, fontSize: 15 }}>
        Este boletim tem o nº de etiqueta <b>{info.numero_inicial}</b>, mas está sem tubito (campo C). Se o boletim não tem tubito mesmo, apague o nº.
        <Button onClick={remover} sx={{ ml: 1, textTransform: 'none', fontWeight: 700, color: C.ambarTexto }}>Apagar nº</Button>
      </Box>
    );
  }

  const qTela = qtdTela === null || qtdTela === undefined || qtdTela === '' ? null : Number(qtdTela);
  const mudouQtd = qTela !== null && !Number.isNaN(qTela) && qTela !== info.qtd;
  const ultimoAnterior = info.sugestao ? (info.sugestao === 1 ? 999 : info.sugestao - 1) : null;
  const avisos = conf?.avisos || [];
  const temAviso = avisos.length > 0;
  const numeros = conf?.numeros || [];
  const voltou = numeros.some((n, k) => k > 0 && numeros[k - 1] === 999 && n === 1);
  const estaSalvo = salvo !== null && numero === salvo && !pendente;
  const nome = info.agente_nome || 'o agente';

  return (
    <Box sx={{ ...caixa, border: temAviso ? `2px solid ${C.ambar}` : `1px solid ${C.borda}`, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box component="h3" sx={{ m: 0, fontSize: 17 }}>Etiquetas dos tubitos · {nome}</Box>
        {estaSalvo && <Box component="span" sx={{ px: 1.25, py: 0.5, borderRadius: 999, bgcolor: C.verdeFundo, color: C.verdeTexto, fontSize: 13, fontWeight: 700 }}>✓ Salvo</Box>}
        {!estaSalvo && pendente && <Box component="span" sx={{ px: 1.25, py: 0.5, borderRadius: 999, bgcolor: C.seloAmbar, color: C.seloAmbarTexto, fontSize: 13, fontWeight: 700 }}>Ainda não salvo</Box>}
        {!estaSalvo && !pendente && !confirmado && <Box component="span" sx={{ px: 1.25, py: 0.5, borderRadius: 999, bgcolor: C.cinzaSelo, color: C.cinzaTexto, fontSize: 13, fontWeight: 700 }}>Confira com a etiqueta</Box>}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <label htmlFor={`tubitos-n1-${fad01Id}`} style={{ fontSize: 15, fontWeight: 700 }}>Nº da 1ª etiqueta</label>
          <input
            id={`tubitos-n1-${fad01Id}`}
            inputMode="numeric"
            value={valor}
            onChange={(e) => { setValor(e.target.value.replace(/\D/g, '').slice(0, 3)); setConfirmado(true); }}
            style={{ width: 150, height: 52, boxSizing: 'border-box', padding: '0 14px', border: `2px solid ${temAviso ? '#C98A1B' : confirmado ? TUBITOS_COR : C.cinzaBorda}`, borderRadius: 10, fontSize: 28, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: confirmado ? C.texto : C.fraco, background: confirmado ? '#fff' : '#F7F8FA' }}
          />
        </Box>
        <Box sx={{ fontSize: 15, color: C.fraco }}>Tubitos neste boletim (C): <b style={{ color: C.texto }}>{info.qtd}</b></Box>
      </Box>

      {!confirmado && info.sugestao !== null && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, fontSize: 15, color: C.suave }}>
          <span>Sugestão do TOVIS: o último de {nome} foi o <b>{ultimoAnterior}</b>. Confira na etiqueta.</span>
          <Button variant="outlined" onClick={() => setConfirmado(true)} sx={{ minHeight: 44, borderRadius: '10px', textTransform: 'none', fontWeight: 700, color: TUBITOS_COR, borderColor: TUBITOS_COR }}>✓ Confere com a etiqueta</Button>
        </Box>
      )}
      {info.sugestao === null && salvo === null && (
        <Box sx={{ fontSize: 15, color: C.suave }}>Primeiro registro de {nome}: digite o nº da 1ª etiqueta.</Box>
      )}

      {numeros.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box component="span" sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', color: C.fraco }}>ETIQUETAS DESTE BOLETIM</Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {numeros.map((n, k) => (
              <Box key={k} component="span" sx={{ px: 1.75, py: 0.75, borderRadius: '8px', fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums', bgcolor: temAviso ? C.ambarFundo : C.verdeChip, color: temAviso ? C.ambarTexto : C.verdeTexto }}>{n}</Box>
            ))}
          </Box>
        </Box>
      )}

      {avisos.map((a, k) => (
        <Box key={k} sx={{ bgcolor: C.ambarFundo, borderRadius: '10px', p: '10px 12px', fontSize: 15, lineHeight: 1.4, color: C.ambarTexto }}>
          {a.texto} Se estiver certo, salve mesmo assim.
        </Box>
      ))}
      {voltou && (
        <Box sx={{ bgcolor: C.azulFundo, borderRadius: '10px', p: '10px 12px', fontSize: 15, color: C.azulTexto }}>
          Depois do 999, a numeração volta ao 1 (regra do FAD-02). O TOVIS conta sozinho.
        </Box>
      )}
      {mudouQtd && (
        <Box sx={{ bgcolor: C.azulFundo, borderRadius: '10px', p: '10px 12px', fontSize: 15, color: C.azulTexto }}>
          A quantidade de tubitos na tela ({qTela}) está diferente da salva ({info.qtd}). Salve o boletim para o TOVIS recalcular as etiquetas.
        </Box>
      )}
      {erro && <Box sx={{ color: C.vermelho, fontSize: 14 }}>{erro}</Box>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
        <Button variant="contained" disableElevation onClick={salvar} disabled={!valido || salvando || estaSalvo}
          sx={{ minHeight: 44, borderRadius: '10px', bgcolor: TUBITOS_COR, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#0B5A54' } }}>
          {salvando ? 'Salvando…' : 'Salvar nº'}
        </Button>
        <Box component="span" sx={{ fontSize: 14, color: C.fraco }}>Depois de confirmado, também salva junto com o boletim.</Box>
        {salvo !== null && (
          <Button onClick={remover} sx={{ ml: 'auto', minHeight: 44, textTransform: 'none', color: C.fraco }}>Apagar nº</Button>
        )}
      </Box>
    </Box>
  );
}
